import {getAuthTokenKey, getBackendUrl} from "../../utils/config";

const API_PATH = '/api/kid-mjpeg-streaming';
const PUBLIC_PATH = '/public/kid-mjpeg-streaming';

/**
 * Generate a temporary token for kid MJPEG streaming
 * @param {number} childId - The child ID to generate token for
 * @returns {Promise<Object>} - Response with the generated token
 */
export const generateMJPEGStreamingToken = async (childId) => {
    try {
        const baseUrl = getBackendUrl();
        const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        const url = `${cleanBaseUrl}${API_PATH}/${childId}/token`;

        console.log('Generating MJPEG streaming token for child ID:', childId, 'URL:', url);

        // Get JWT token from localStorage for authentication
        const authTokenKey = getAuthTokenKey();
        const jwtToken = localStorage.getItem(authTokenKey);

        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        };

        // Add Authorization header if JWT token is available
        if (jwtToken) {
            headers['Authorization'] = `Bearer ${jwtToken}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: headers
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Generated MJPEG streaming token:', data);
        return data;
    } catch (err) {
        console.error('Failed to generate MJPEG streaming token for child ID:', childId, err);
        throw new Error(err.message || 'Failed to generate streaming token');
    }
};

/**
 * Get active cameras for a child
 * Requires JWT authentication - uses Authorization header
 * @param {number} childId - The child ID
 * @returns {Promise<Object>} - Response with active cameras
 */
export const getChildMappedCamerasMJPEG = async (childId) => {
    try {
        const baseUrl = getBackendUrl();
        const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        // Use API endpoint - requires authentication, no token param needed
        const url = `${cleanBaseUrl}${API_PATH}/${childId}/active-camera`;

        console.log('Getting active cameras for child ID:', childId, 'URL:', url);

        // Get JWT token from localStorage for authentication
        const authTokenKey = getAuthTokenKey();
        const jwtToken = localStorage.getItem(authTokenKey);

        if (!jwtToken) {
            throw new Error('Authentication token not found. Please login.');
        }

        const headers = {
            'Accept': 'application/json',
            'Authorization': `Bearer ${jwtToken}`
        };

        const response = await fetch(url, {
            method: 'GET',
            headers: headers
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Got active cameras:', data);
        return data;
    } catch (err) {
        console.error('Failed to get active cameras for child ID:', childId, err);
        throw new Error(err.message || 'Failed to get active cameras');
    }
};

/**
 * Get MJPEG stream URL for a child and camera
 * @param {number} childId - The child ID
 * @param {number} cameraId - The camera ID
 * @param {string} token - The streaming token
 * @param {string} userId - User ID for connection tracking
 * @returns {string} - The MJPEG stream URL
 */
export const getKidCameraMJPEGStreamUrl = (childId, cameraId, token, userId = 'anonymous') => {
    const baseUrl = getBackendUrl();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    // Use public endpoint for streaming - requires token in query param
    const streamUrl = `${cleanBaseUrl}${PUBLIC_PATH}/${childId}/camera/${cameraId}/stream?token=${encodeURIComponent(token)}&userId=${encodeURIComponent(userId)}`;

    console.log('=== MJPEG Stream URL Construction ===');
    console.log('Base URL:', baseUrl);
    console.log('Clean Base URL:', cleanBaseUrl);
    console.log('Public Path:', PUBLIC_PATH);
    console.log('Child ID:', childId);
    console.log('Camera ID:', cameraId);
    console.log('Token:', token ? `${token.substring(0, 10)}...` : 'null');
    console.log('User ID:', userId);
    console.log('Final Stream URL:', streamUrl);
    console.log('=====================================');
    return streamUrl;
};

